import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { ChevronLeft, ChevronRight, Calendar, CalendarOff, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import PlannerItemModal from '../../../components/planner/PlannerItemModal';
import DayDetailSheet from '../../../components/planner/DayDetailSheet';
import { plannerAPI, integrationsAPI, settingsAPI } from '../../../api';
import { useToast } from '../../../hooks/useToast';

const COLOR_MAP = {
  blue: '#3b82f6',
  red: '#ef4444',
  green: '#10b981',
  yellow: '#f59e0b',
  purple: '#8b5cf6',
  orange: '#f97316',
  gray: '#6b7280',
};

const GOOGLE_CALENDAR_COLOR = '#4285F4';

const TAB_CALENDAR = 'calendar';
const TAB_PLANNER = 'planner';

export default function MobilePlanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();
  const calendarRef = useRef(null);
  const [items, setItems] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [daySheetOpen, setDaySheetOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [draftSelection, setDraftSelection] = useState(null);
  const [currentTitle, setCurrentTitle] = useState('');
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [integrationStatusLoading, setIntegrationStatusLoading] = useState(true);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_CALENDAR);
  const toastFired = useRef({ connected: false, error: false });

  const showGoogleEvents = googleConnected && activeTab === TAB_CALENDAR;

  const events = useMemo(() => {
    const plannerEventList = items.map((item) => {
      const colorValue = COLOR_MAP[item.color] || COLOR_MAP.blue;
      return {
        id: item.id,
        title: item.title,
        start: item.start_at,
        end: item.end_at,
        allDay: item.all_day,
        backgroundColor: colorValue,
        borderColor: colorValue,
        textColor: '#ffffff',
        classNames: item.is_done ? ['opacity-70', 'line-through'] : [],
        extendedProps: item,
      };
    });
    if (!showGoogleEvents || !googleEvents.length) return plannerEventList;
    const googleEventList = googleEvents.map((ev) => ({
      id: ev.id,
      title: ev.title,
      start: ev.start,
      end: ev.end,
      allDay: ev.allDay ?? false,
      backgroundColor: ev.backgroundColor ?? GOOGLE_CALENDAR_COLOR,
      borderColor: ev.backgroundColor ?? GOOGLE_CALENDAR_COLOR,
      textColor: '#ffffff',
      classNames: ['fc-google-event'],
      extendedProps: { ...ev, source: 'google' },
    }));
    return [...plannerEventList, ...googleEventList];
  }, [items, googleEvents, showGoogleEvents]);

  const fetchItems = async (range, opts = {}) => {
    if (!range?.startStr || !range?.endStr) return;
    const includeGoogle = opts.includeGoogle ?? showGoogleEvents;
    try {
      setLoading(true);
      const data = await plannerAPI.getItems({
        from: range.startStr,
        to: range.endStr,
        includeGoogle,
      });
      setItems(data.items || []);
      setGoogleEvents(data.googleEvents || []);
    } catch (error) {
      console.error('Failed to load planner items', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatesSet = (arg) => {
    fetchItems({ startStr: arg.startStr, endStr: arg.endStr });
    setCurrentTitle(arg.view.title);
  };

  const openCreateModal = (selection) => {
    setSelectedItem(null);
    setDraftSelection(selection || null);
    setModalOpen(true);
  };

  const handleDateClick = (arg) => {
    setSelectedDate(arg.dateStr);
    setDaySheetOpen(true);
  };

  const handleAddEventForDate = (dateStr) => {
    if (!dateStr) return;
    setDaySheetOpen(false);
    const startAt = `${dateStr}T00:00:00`;
    const endAt = `${dateStr}T23:59:59`;
    openCreateModal({
      startAt,
      endAt,
      allDay: true,
    });
  };

  const handleDaySheetEventClick = (item) => {
    setDaySheetOpen(false);
    setSelectedItem(item);
    setDraftSelection(null);
    setModalOpen(true);
  };

  const handleSelect = (selectionInfo) => {
    openCreateModal({
      startAt: selectionInfo.startStr,
      endAt: selectionInfo.endStr,
      allDay: selectionInfo.allDay,
    });
  };

  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps?.source === 'google') return;
    setSelectedItem(clickInfo.event.extendedProps);
    setDraftSelection(null);
    setModalOpen(true);
  };

  const handleSave = async (payload) => {
    if (selectedItem) {
      const updated = await plannerAPI.updateItem(selectedItem.id, payload);
      setItems((prev) =>
        prev.map((item) => (item.id === selectedItem.id ? updated.item : item))
      );
      setSelectedItem(updated.item);
    } else {
      const created = await plannerAPI.createItem(payload);
      setItems((prev) => [...prev, created.item]);
    }
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    await plannerAPI.deleteItem(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    setModalOpen(false);
  };

  const handleToggleDone = async (id) => {
    const updated = await plannerAPI.toggleDone(id);
    setItems((prev) =>
      prev.map((item) => (item.id === id ? updated.item : item))
    );
    setSelectedItem(updated.item);
  };

  const handleAcceptAssignment = async (id) => {
    const updated = await plannerAPI.acceptAssignment(id);
    setItems((prev) => {
      const exists = prev.some((item) => item.id === updated.item.id);
      if (exists) {
        return prev.map((item) => (item.id === updated.item.id ? updated.item : item));
      }
      return [...prev, updated.item];
    });
    setSelectedItem(updated.item);
    setModalOpen(false);
  };

  const handleRejectAssignment = async (id) => {
    await plannerAPI.rejectAssignment(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    setModalOpen(false);
  };

  const handlePrev = () => {
    const api = calendarRef.current?.getApi();
    if (api) api.prev();
  };

  const handleNext = () => {
    const api = calendarRef.current?.getApi();
    if (api) api.next();
  };

  const handleToday = () => {
    const api = calendarRef.current?.getApi();
    if (api) api.today();
  };

  const handleConnectGoogle = async () => {
    try {
      const { authUrl } = await integrationsAPI.getGoogleAuthUrl();
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        toastError('Connect failed', 'Could not get Google Calendar authorization URL.');
      }
    } catch (err) {
      toastError('Connect failed', err?.message || 'Could not connect Google Calendar.');
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      setDisconnectLoading(true);
      await integrationsAPI.disconnectGoogle();
      setGoogleConnected(false);
      setGoogleEvents([]);
      toastSuccess('Disconnected', 'Google Calendar has been disconnected.');
      const api = calendarRef.current?.getApi();
      if (api) {
        fetchItems({
          startStr: api.view.activeStart.toISOString(),
          endStr: api.view.activeEnd.toISOString(),
        });
      }
    } catch (err) {
      toastError('Disconnect failed', err?.message || 'Could not disconnect Google Calendar.');
    } finally {
      setDisconnectLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setIntegrationStatusLoading(true);
    Promise.all([integrationsAPI.getGoogleStatus(), settingsAPI.getGoogleCalendarIntegration()])
      .then(([status, config]) => {
        if (!cancelled) {
          setGoogleConnected(!!status?.connected);
          const configured = !!(config?.hasClientId && config?.hasClientSecret);
          setGoogleConfigured(configured);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleConnected(false);
          setGoogleConfigured(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIntegrationStatusLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const connected = searchParams.get('google_connected');
    const errMsg = searchParams.get('google_error');
    if (connected === '1' && !toastFired.current.connected) {
      toastFired.current.connected = true;
      toastSuccess('Google Calendar connected', 'Your Google Calendar events will appear.');
      setGoogleConnected(true);
      setActiveTab(TAB_CALENDAR);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('google_connected');
        return next;
      }, { replace: true });
      const api = calendarRef.current?.getApi();
      if (api) {
        fetchItems(
          { startStr: api.view.activeStart.toISOString(), endStr: api.view.activeEnd.toISOString() },
          { includeGoogle: true }
        );
      }
    }
    if (errMsg && !toastFired.current.error) {
      toastFired.current.error = true;
      toastError('Google connection failed', decodeURIComponent(errMsg));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('google_error');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, toastSuccess, toastError]);

  const tabChangeRef = useRef(true);
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (tabChangeRef.current) {
      tabChangeRef.current = false;
      return;
    }
    fetchItems({
      startStr: api.view.activeStart.toISOString(),
      endStr: api.view.activeEnd.toISOString(),
    });
  }, [activeTab, showGoogleEvents]);

  return (
    <div className="px-4 py-4 pb-8">
      <ToastContainer />
      <div className="space-y-4">
        <div className="flex flex-col gap-3">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Planner
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            {!integrationStatusLoading && googleConfigured && !googleConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnectGoogle}
                className="h-9 gap-1.5 text-sm"
                aria-label="Connect Google Calendar"
              >
                <Calendar className="h-4 w-4" />
                Connect Google
              </Button>
            )}
            {googleConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnectGoogle}
                disabled={disconnectLoading}
                className="h-9 gap-1.5 text-sm border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                aria-label="Disconnect Google Calendar"
              >
                {disconnectLoading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <CalendarOff className="h-4 w-4" />
                )}
                Disconnect
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => openCreateModal()}
              className="h-9 gap-1.5 text-sm"
            >
              <Plus className="h-4 w-4" />
              New Item
            </Button>
          </div>
        </div>

        {googleConnected && (
          <div className="flex gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-1">
            <button
              type="button"
              onClick={() => setActiveTab(TAB_CALENDAR)}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === TAB_CALENDAR
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(TAB_PLANNER)}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === TAB_PLANNER
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Planner
            </button>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrev}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                aria-label="Previous"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                aria-label="Next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleToday}
                className="ml-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300"
              >
                Today
              </button>
            </div>
            <h2 className="flex-1 min-w-0 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {currentTitle}
            </h2>
            <div className="w-[88px]" aria-hidden />
          </div>
          <div className="p-2">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={false}
              height="auto"
              selectable
              selectMirror
              events={events}
              dateClick={handleDateClick}
              select={handleSelect}
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              dayMaxEvents={4}
              nowIndicator
              eventDisplay="block"
            />
          </div>
        </div>
      </div>

      <DayDetailSheet
        open={daySheetOpen}
        onOpenChange={setDaySheetOpen}
        selectedDate={selectedDate}
        events={events}
        onAddEvent={handleAddEventForDate}
        onEventClick={handleDaySheetEventClick}
      />

      <PlannerItemModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        item={selectedItem}
        draftSelection={draftSelection}
        onSave={handleSave}
        onDelete={handleDelete}
        onToggleDone={handleToggleDone}
        onAcceptAssignment={handleAcceptAssignment}
        onRejectAssignment={handleRejectAssignment}
      />
    </div>
  );
}
